import React from 'react';
import {ScrollView} from '@genestack/ui';
import {ContinuantsGroupFoldingItem} from './continuants-group-folding-item';
import {RightPanelListItem} from '../right-panel-list-item';
import {useFoldingItemsState} from '../../../../../../hooks/use-folding-items-state';
import {GroupedContinuantsMap} from '../interface';
import {EmptyList} from '../../../../../../components/empty-list/empty-list';
import {NothingFoundView} from './nothing-found-view';
import {matchString, SearchResultChildrenMaps} from './use-filter-stats';
import {MatchedSynonym} from './matched-synonym';
import {ShowMoreButton} from './show-more-button';

interface Props {
    selectedGraphNodes: number[];
    setHoveredNodes: (nodeIds: number[]) => void;
    selectGraphNodes: (nodes: number[], isSelectMultiple: boolean) => void;
    groupedContinuantsMap: GroupedContinuantsMap;
    searchString: string;
    searchResultChildrenMaps?: SearchResultChildrenMaps;
    foldingStateProps: ReturnType<typeof useFoldingItemsState>;
    irrelevantShownProps: ReturnType<typeof useFoldingItemsState>;
}

export function ContinuantsStatistics(props: Props) {
    function getFoldedGroupSettings(group: string) {
        if (props.searchString && props.searchResultChildrenMaps) {
            const [matchedChildren, irrelevantChildren] = [
                props.searchResultChildrenMaps.continuantGroupsMatchedChildren[group] || [],
                props.searchResultChildrenMaps.continuantGroupsIrrelevantChildren[group] || []
            ];
            const groupNameMatches = matchString(group, props.searchString);
            const hasShowMoreButton =
                groupNameMatches && !!matchedChildren.length && !!irrelevantChildren.length;

            const isIrrelevantChildrenShown = hasShowMoreButton
                ? props.irrelevantShownProps.foldingState[group]
                : groupNameMatches;

            return {
                renderedChildren: [
                    ...matchedChildren,
                    ...(isIrrelevantChildrenShown ? irrelevantChildren : [])
                ],
                hasShowMoreButton,
                isUnfoldedByDefault: !!matchedChildren.length
            };
        }

        return {
            renderedChildren: props.groupedContinuantsMap[group],
            hasShowMoreButton: false,
            isUnfoldedByDefault: false
        };
    }

    if (!Object.keys(props.groupedContinuantsMap).length) {
        if (props.searchString) {
            return <NothingFoundView searchString={props.searchString} />;
        }

        return <EmptyList message="Nothing found" />;
    }

    return (
        <ScrollView>
            {Object.keys(props.groupedContinuantsMap).map((group) => {
                const {renderedChildren, isUnfoldedByDefault, hasShowMoreButton} =
                    getFoldedGroupSettings(group);

                const isIrrelevantChildrenShown = props.irrelevantShownProps.foldingState[group];

                return (
                    <ContinuantsGroupFoldingItem
                        key={group}
                        label={group}
                        continuants={props.groupedContinuantsMap[group]}
                        foldingStateProps={props.foldingStateProps}
                        foldingStateKey={group}
                        padding={1}
                        searchString={props.searchString}
                        isUnfoldedByDefault={isUnfoldedByDefault}
                    >
                        {renderedChildren.map((continuantStatRecord) => (
                            <RightPanelListItem
                                label={continuantStatRecord.label}
                                count={continuantStatRecord.occurrencesCount}
                                key={`${continuantStatRecord.documentAccession}${continuantStatRecord.elementNodeId}`}
                                active={
                                    // if graph is empty graphViewNodeIds length is 0
                                    !!props.selectedGraphNodes.length &&
                                    continuantStatRecord.graphViewNodeIds.every((id) =>
                                        props.selectedGraphNodes.includes(id)
                                    )
                                }
                                setHoveredNodes={props.setHoveredNodes}
                                selectGraphNodes={props.selectGraphNodes}
                                graphViewNodeIds={continuantStatRecord.graphViewNodeIds}
                                padding={2}
                                searchString={props.searchString}
                                append={
                                    <MatchedSynonym
                                        continuant={continuantStatRecord}
                                        searchValue={props.searchString}
                                    />
                                }
                            />
                        ))}

                        {hasShowMoreButton && (
                            <ShowMoreButton
                                isShowingMore={isIrrelevantChildrenShown}
                                onClick={() => props.irrelevantShownProps.toggleFoldingState(group)}
                                level={2}
                            />
                        )}
                    </ContinuantsGroupFoldingItem>
                );
            })}
        </ScrollView>
    );
}
